import { useEffect, useRef, useState } from "react";
import useApi from "../../hooks/useApi";
import useAccessScope from "../../hooks/useAccessScope";
import useInterruptibleMutation from "../../hooks/useInterruptibleMutation";
import SimpleSortTable, { TableColumn } from "../../components/BaseComponents/SimpleSortTable/SimpleSortTable";
import { formatDate, getApiErrorMessage, getCSRFToken } from "../../lib/utils";
import { setUserAdminLocked } from "../../lib/adminUserLock";
import Pagination from "../../components/BaseComponents/Pagination/Pagination";
import { Button, InlineAlert } from "../../components/BaseComponents";
import useDataSort from "../../hooks/useDataSort";
import usePagination from "../../hooks/usePagination";
import useCleanup from "../../hooks/useCleanup";
import ConfirmationModal from "../../components/BaseComponents/ConfirmationModal";

type AdminCompanyUsersProps = {
    companyId: string;
}

function AdminCompanyUsers({ companyId }: AdminCompanyUsersProps) {
    const [results, setResults] = useState<any[]>([])

    const [loading, setLoading] = useState(false);
    const dataSort = useDataSort('updated_at', 'desc');
    const pagination = usePagination();

    const [deletionCandidate, setDeletionCandidate] = useState<any>(null);
    // Issue #884: onSearch/onLockUser/onDeleteUser previously had no catch at all, so a
    // rejecting GET/write (including the now-rejecting READ_ONLY_ACCESS/
    // ACCESS_AUDIT_UNAVAILABLE notify-only codes - #857 item 6) left `loading` stuck true (the
    // table and pagination spinners forever) with no visible feedback, and an unhandled
    // rejection.
    const [searchError, setSearchError] = useState('');
    const [lockError, setLockError] = useState('');
    const [deleteError, setDeleteError] = useState('');

    const api = useApi();
    const { generation, isCurrent } = useAccessScope(api, companyId);
    const [loadedGeneration, setLoadedGeneration] = useState<number>();
    const loadController = useRef<AbortController>();
    const startMutation = useInterruptibleMutation(api, companyId,
        'The user access change was interrupted and may already have been applied. Refresh the organisation user list before trying again.');
    const onSearch = useCleanup(() => {
        let cancelled = false;
        const controller = new AbortController();
        loadController.current = controller;
        
        const run = async () => {
            setLoading(true);
            setSearchError('');
            const qs = new URLSearchParams({
                ...pagination.params,
                ...dataSort.params,
            })
            try {
                const apiResult = await api.get(`/admin/company/${companyId}/users?` + qs.toString(), { signal: controller.signal });
                if(cancelled || controller.signal.aborted || !isCurrent(generation)) {
                    return;
                }
                setResults(apiResult.data.data);
                pagination.readMeta(apiResult.data.meta);
            } catch (err) {
                if(cancelled || controller.signal.aborted || !isCurrent(generation)) {
                    return;
                }
                setSearchError(getApiErrorMessage(err, 'Failed to load users. Please try again.'));
            } finally {
                if(!cancelled && !controller.signal.aborted && isCurrent(generation)) {
                    setLoading(false);
                }
            }
        }
        run()
        return () => {
            cancelled = true;
            controller.abort();
        }
    });

    const onLockUser = async (userId: string, locked: boolean) => {
        if (loadedGeneration !== generation || loading) return;
        const { signal, onSubmitted, finish } = startMutation();
        let refreshing = false;
        setLoading(true);
        setLockError('');
        try {
            await setUserAdminLocked(api, userId, locked, {
                signal,
                onSubmitted: () => {
                    if (!isCurrent(generation)) throw new DOMException('The operation was aborted.', 'AbortError');
                    onSubmitted();
                },
            })
            if (signal.aborted || !isCurrent(generation)) return;
            finish();
            refreshing = true;
            onSearch();
        } catch (err) {
            if (!signal.aborted && isCurrent(generation)) {
                setLockError(getApiErrorMessage(err, 'Failed to update the lock status. Please try again.'));
            }
        } finally {
            finish();
            if (!refreshing && !signal.aborted && isCurrent(generation)) setLoading(false);
        }
    }

    const onDeleteUser = async (userId: string) => {
        if (loadedGeneration !== generation || loading) return;
        const { signal, onSubmitted, finish } = startMutation();
        let refreshing = false;
        setLoading(true);
        setDeletionCandidate(null);
        setDeleteError('');
        try {
            const csrf = await getCSRFToken(signal);
            if (signal.aborted || !isCurrent(generation)) return;
            onSubmitted();
            await api.delete(`/admin/user/${userId}`, {
                _csrf: csrf,
            }, { signal })
            if (signal.aborted || !isCurrent(generation)) return;
            finish();
            refreshing = true;
            onSearch();
        } catch (err) {
            if (!signal.aborted && isCurrent(generation)) {
                setDeleteError(getApiErrorMessage(err, 'Failed to delete this user. Please try again.'));
            }
        } finally {
            finish();
            if (!refreshing && !signal.aborted && isCurrent(generation)) setLoading(false);
        }
    }

    useEffect(() => {
        setResults([]);
        setDeletionCandidate(null);
        setLockError('');
        setDeleteError('');
        pagination.onPageChange(1);
        pagination.readMeta({ ...pagination.pagination, total: 0, first_page: 1, last_page: 1 });
        return () => loadController.current?.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [generation])

    useEffect(() => {
        if (loadedGeneration !== generation && pagination.pagination.current_page === 1 && pagination.params.page === '1') {
            setLoadedGeneration(generation);
        }
    }, [generation, loadedGeneration, pagination.pagination.current_page, pagination.params.page])

    useEffect(() => {
        if (loadedGeneration !== generation) return;
        onSearch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api, companyId, generation, isCurrent, loadedGeneration, pagination.params, dataSort.params])
    
    const dateRenderer = (date: string) => {
        return <span title={date}>{ formatDate(date) }</span>
    }
    const columns: TableColumn[] = [{
        id: 'full_name',
        label: 'Full Name',
        sortable: true,
        renderer: (key, row) => <span>{ row[key] }{ row.row_type === 'invitation' ? <span className="text-charcoal-300"> (pending)</span> : '' }</span>,
    },{
        id: 'email',
        label: 'Email',
        sortable: true,
    }, {
        id: 'role.',
        label: 'Role',
        renderer: (key, row) => <span>{ row.role?.name ?? '-' }</span>
    }, {
        id: 'email_confirmed',
        label: 'Email Confirmed',
        renderer: (key, row) => <span>{ row[key] ? 'Yes' : 'No' }</span>,
        sortable: true,
    }, {
        id: 'created_at',
        label: 'Created',
        renderer: (key, row) => dateRenderer(row[key]),
        sortable: true,
    }, {
        id: 'updated_at', 
        label: 'Updated At',
        renderer: (key, row) => dateRenderer(row[key]),
        sortable: true,
    }, {
        id: 'admin_locked',
        label: 'Locked',
        renderer: (key, row) => {
            if (row.row_type === 'invitation') {
                return <span>-</span>;
            }
            const userId = row['id'];
            const isLocked = row['admin_locked'];
            return (
                <Button
                    type="Primary"
                    size="sm"
                    text={`${isLocked ? 'Unlock' : 'Lock'} User`}
                    onClick={() => onLockUser(userId, !isLocked)}
                />
            );
        },
        sortable: true,
    }, {
        id: 'delete',
        label: 'Delete',
        renderer: (key, row) => {
            const isInvitation = row.row_type === 'invitation';
            return (
                <Button
                    type="Primary"
                    size="sm"
                    text={isInvitation ? 'Revoke invite' : 'Delete user'}
                    onClick={() => setDeletionCandidate(row)}
                />
            );
        },
        sortable: true,
    }]
    const currentScope = loadedGeneration === generation;
    return (
        <div>
            <h3 className="text-white mb-3 mt-8">Users</h3>
            { currentScope && searchError && <InlineAlert type='error' className='mb-4'>{ searchError }</InlineAlert> }
            { currentScope && lockError && <InlineAlert type='error' className='mb-4'>{ lockError }</InlineAlert> }
            { currentScope && deleteError && <InlineAlert type='error' className='mb-4'>{ deleteError }</InlineAlert> }
            <SimpleSortTable
                columns={columns}
                data={currentScope ? results : []}
                isLoading={loading || !currentScope}
				defaultSorting={dataSort.defaultSorting}
				onSortPress={dataSort.onChange}
            />
			<Pagination 
				onPageChange={pagination.onPageChange}
				pagination={pagination.pagination}
                isLoading={loading || !currentScope}
			/>
            <ConfirmationModal
                open={currentScope && !!deletionCandidate}
                onCancel={() => setDeletionCandidate(null)}
                onConfirm={() => currentScope && deletionCandidate && onDeleteUser(deletionCandidate.id)}
                title={deletionCandidate ? (deletionCandidate.row_type === 'invitation' ? 'Revoke invite?' : 'Delete user?') : ''}
            >
                { deletionCandidate && (
                    <div>
                        <p className="text-white mt-8">User name:{ deletionCandidate.full_name}</p>
                        <p className="text-white">User email:{ deletionCandidate.email}</p>
                        <p className="text-white">
                            { deletionCandidate.row_type === 'invitation'
                                ? 'This will revoke the open invitation.'
                                : 'This permanently deletes the account and removes every organisation membership. Any organisation this leaves with no members is deleted if it is a training or sample organisation, or locked if it is a real organisation - organisations shared with other members are unaffected. This cannot be undone.' }
                        </p>
                    </div>
                )}
            </ConfirmationModal>
        </div>
    )
}

export default AdminCompanyUsers;
